/* eslint-disable prettier/prettier */
import React, { useEffect, useMemo, useState } from "react";
import pageBg from "assets/images/plain-img5.jpg";
import cardBg from "assets/images/plain-img3.jpg";

import MKTypography from "../components/MKTypography";
import MKBox from "../components/MKBox";
import MKButton from "../components/MKButton";

import {
  Container,
  Grid,
  Card,
  CardContent,
  MenuItem,
  Select,
  Box,
} from "@mui/material";

import translations from "../i18n/translations";
import { useLanguage } from "../context/LanguageContext";

// 🔁 Google Sheet API URL
const SHEET_URL =
  "https://opensheet.elk.sh/1GcIIHgSWCZfvcgrPL9qHKmrzl4d8A15sY7Z5l75LnZE/Hunnime Pooje";

// 📅 Date helpers
function formatDate(dateStr, t) {
  const date = new Date(dateStr);
  return `${t.months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

function getDayName(dateStr, t) {
  const date = new Date(dateStr);
  return t.days[date.getDay()];
}

function HunnimePooje() {
  const { lang } = useLanguage();
  const t = translations[lang].hunnimepooje;

  const currentYear = new Date().getFullYear().toString();

  const [poojes, setPoojes] = useState([]);
  const [year, setYear] = useState(currentYear);
  const [expandedIndex, setExpandedIndex] = useState(null);

  const toggleExpand = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  useEffect(() => {
    fetch(SHEET_URL)
      .then((res) => res.json())
      .then((data) => {
        const sorted = data.sort(
          (a, b) => new Date(b.Date) - new Date(a.Date)
        );
        setPoojes(sorted);
      });
  }, []);

  // 🔹 Available years
  const years = useMemo(() => {
    const y = poojes.map(p => new Date(p.Date).getFullYear().toString());
    return [...new Set(y)].sort((a, b) => b - a);
  }, [poojes]);

  // 🔹 Filter by year
  const filteredPoojes = useMemo(() => {
    return poojes.filter(p =>
      new Date(p.Date).getFullYear().toString() === year
    );
  }, [poojes, year]);

  // 🔹 Find upcoming pooje
  const today = new Date();

  const upcomingPooje = useMemo(() => {
    const sorted = [...poojes].sort(
      (a, b) => new Date(a.Date) - new Date(b.Date)
    );

    return sorted.find(p => new Date(p.Date) >= today);
  }, [poojes]);


  return (
    <MKBox
      sx={{
        minHeight: "100vh",
        backgroundImage: `linear-gradient(
          rgba(239, 212, 156, 0.9),
          rgba(245, 198, 104, 0.9)
        ), url(${pageBg})`,
        backgroundSize: "cover",
        py: 6,
      }}
    >
      <Container maxWidth="lg">
        {/* Intro */}
        <MKTypography textAlign="center" fontWeight="bold" mb={2} fontSize={18}>
          {t.brief}
        </MKTypography>

        {/* Title */}
        <MKTypography variant="h5" textAlign="center" fontWeight="bold" mb={3}>
          {t.title}
        </MKTypography>

        {/* Year Filter */}
        <Box
          textAlign="center"
          mb={5}
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1,
              borderRadius: "30px",
              background:
                "linear-gradient(135deg, #fff3c4, #f7d774, #e6b84e)",
              boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
              border: "1px solid #c9a227",
            }}
          >
            <Select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              size="small"
              disableUnderline
              sx={{
                fontWeight: "bold",
                fontSize: 15,
                color: "#4e2e00",
                minWidth: 120,
                "& .MuiSelect-icon": {
                  color: "#4e2e00",
                },
              }}
            >
              {years.map((y) => (
                <MenuItem
                  key={y}
                  value={y}
                  sx={{ fontWeight: "bold" }}
                >
                  {y}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>


        <Grid container spacing={3}>
          {filteredPoojes.map((item, index) => {
            const isUpcoming =
              upcomingPooje &&
              new Date(item.Date).toDateString() ===
              new Date(upcomingPooje.Date).toDateString();

            const sponsorText =
              lang === "en"
                ? item.Sponsor_EN || item.Sponsor
                : item.Sponsor || item.Sponsor_EN;

            const galleryLink = item.Gallery_Link;
            const videoLink = item.Video_Link;

            return (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 4,
                    boxShadow: isUpcoming
                      ? "0 0 18px rgba(255,140,0,0.9)"
                      : "0 12px 30px rgba(91,82,148,0.35)",
                    border: isUpcoming
                      ? "3px solid #ff9800"
                      : "none",
                    backgroundImage: isUpcoming
                      ? `linear-gradient(
                      rgba(135, 227, 144, 0.48),
                      rgba(56, 178, 83, 0.9)
                    ), url(${cardBg})` : `linear-gradient(
                      rgba(218,178,70,0.48),
                      rgba(231,211,110,0.9)
                    ), url(${cardBg})`,
                    backgroundSize: "cover",
                  }}
                >
                  <CardContent>
                    {/* Date */}
                    <MKTypography fontWeight="bold" variant="h5">
                      🌕 {formatDate(item.Date, t)}
                    </MKTypography>

                    <MKTypography fontWeight="bold" fontSize={14}>
                      📆 {getDayName(item.Date, t)}
                    </MKTypography>

                    <MKTypography fontWeight="bold" fontSize={14} mt={1}>
                      {t.timeLabel}: {item.Time}
                    </MKTypography>

                    {/* Sponsors */}
                    <MKTypography fontWeight="bold" fontSize={18} mt={3} color="#4e2e00">
                      {t.sponsorsLabel}
                    </MKTypography>

                    <MKTypography fontSize={14} fontWeight="bold"
                      sx={{
                        maxHeight:
                          expandedIndex === index ? "none" : 48,
                        overflow: "hidden",
                      }}
                    >
                      {sponsorText}
                    </MKTypography>

                    {sponsorText?.length > 60 && (
                      <MKTypography
                        onClick={() => toggleExpand(index)}
                        sx={{ cursor: "pointer", fontSize: 10 }}
                      >
                        {expandedIndex === index ? "View Less ↑" : "View More ↓"}
                      </MKTypography>
                    )}

                    {/* 🔗 Buttons */}
                    {(galleryLink || videoLink) && (
                      <Box mt={3} display="flex" gap={1} flexWrap="wrap">
                        {galleryLink && (
                          <MKButton
                            size="small"
                            color="warning"
                            onClick={() => window.open(galleryLink, "_blank")}
                          >
                            📸 Gallery
                          </MKButton>
                        )}

                        {videoLink && (
                          <MKButton
                            size="small"
                            color="error"
                            onClick={() => window.open(videoLink, "_blank")}
                          >
                            ▶️ Video
                          </MKButton>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </MKBox>
  );
}

export default HunnimePooje;
